import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminLayout } from "@/components/blog/AdminLayout";
import { ProtectedRoute } from "@/components/blog/ProtectedRoute";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Save, Eye, Code, Mail } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export default function AdminEmailTemplates() {
  const queryClient = useQueryClient();
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");

  // Fetch templates
  const { data: templates, isLoading } = useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Update template
  const updateTemplate = useMutation({
    mutationFn: async (templateData: { id: string; subject: string; html_content: string }) => {
      const { data, error } = await supabase
        .from("email_templates")
        .update({
          subject: templateData.subject,
          html_content: templateData.html_content,
          updated_at: new Date().toISOString(),
        })
        .eq("id", templateData.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Template Updated",
        description: "The email template has been saved successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update template",
        variant: "destructive",
      });
    },
  });

  const handleSelectTemplate = (template: any) => {
    setSelectedTemplate(template);
    setSubject(template.subject);
    setHtmlContent(template.html_content);
    setPreviewHtml(generatePreview(template.html_content));
  };

  const generatePreview = (html: string) => {
    // Replace template variables with example data for preview
    return html
      .replace(/\{\{quoted_price\}\}/g, "395.00")
      .replace(/\{\{user_phone\}\}/g, "210-555-1234")
      .replace(/\{\{user_email\}\}/g, "customer@example.com")
      .replace(/\{\{#if user_phone\}\}/g, "")
      .replace(/\{\{\/if\}\}/g, "");
  };

  const handleSave = () => {
    if (!selectedTemplate) return;
    updateTemplate.mutate({
      id: selectedTemplate.id,
      subject,
      html_content: htmlContent,
    });
  };

  const handlePreview = () => {
    setPreviewHtml(generatePreview(htmlContent));
  };

  return (
    <ProtectedRoute>
      <AdminLayout>
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Email Templates</h1>
            <p className="text-muted-foreground">
              Customize email templates for automated follow-ups
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Template List */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle>Templates</CardTitle>
                <CardDescription>Select a template to edit</CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <p>Loading templates...</p>
                ) : (
                  <div className="space-y-2">
                    {templates?.map((template) => (
                      <Button
                        key={template.id}
                        variant={selectedTemplate?.id === template.id ? "secondary" : "ghost"}
                        className="w-full justify-start"
                        onClick={() => handleSelectTemplate(template)}
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        <span className="truncate">{template.name}</span>
                        {template.is_active && (
                          <Badge variant="default" className="ml-auto">Active</Badge>
                        )}
                      </Button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Editor */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>
                  {selectedTemplate ? `Edit: ${selectedTemplate.name}` : "Select a Template"}
                </CardTitle>
                <CardDescription>
                  Use template variables like {"{"}{"{"} quoted_price {"}"}{"}"},  {"{"}{"{"} user_phone {"}"}{"}"},  {"{"}{"{"} user_email {"}"}{"}"} 
                </CardDescription>
              </CardHeader>
              <CardContent>
                {!selectedTemplate ? (
                  <p className="text-center text-muted-foreground py-8">
                    Select a template from the list to start editing
                  </p>
                ) : (
                  <div className="space-y-4">
                    {/* Subject Line */}
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject Line</Label>
                      <Input
                        id="subject"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Enter email subject..."
                      />
                    </div>

                    {/* Editor Tabs */}
                    <Tabs defaultValue="edit">
                      <TabsList>
                        <TabsTrigger value="edit">
                          <Code className="h-4 w-4 mr-2" />
                          Edit HTML
                        </TabsTrigger>
                        <TabsTrigger value="preview">
                          <Eye className="h-4 w-4 mr-2" />
                          Preview
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="edit" className="space-y-2">
                        <Label htmlFor="html-content">HTML Content</Label>
                        <Textarea
                          id="html-content"
                          value={htmlContent}
                          onChange={(e) => setHtmlContent(e.target.value)}
                          placeholder="Enter HTML content..."
                          className="min-h-[400px] font-mono text-sm"
                        />
                        <p className="text-xs text-muted-foreground">
                          💡 Tip: Use inline styles for best email client compatibility
                        </p>
                      </TabsContent>

                      <TabsContent value="preview">
                        <div className="border rounded-lg p-4 bg-white min-h-[400px] overflow-auto">
                          {previewHtml ? (
                            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                          ) : (
                            <p className="text-center text-muted-foreground py-8">
                              Click "Generate Preview" to see how your email will look
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          className="mt-2"
                          onClick={handlePreview}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Generate Preview
                        </Button>
                      </TabsContent>
                    </Tabs>

                    {/* Variables Info */}
                    <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm text-blue-900 dark:text-blue-100">
                          Available Variables
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-blue-800 dark:text-blue-200">
                        <ul className="list-disc list-inside space-y-1">
                          <li><code>{"{"}{"{"} quoted_price {"}"}{"}"}  </code> - The quoted price amount</li>
                          <li><code>{"{"}{"{"} user_phone {"}"}{"}"}  </code> - Customer's phone number</li>
                          <li><code>{"{"}{"{"} user_email {"}"}{"}"}  </code> - Customer's email address</li>
                        </ul>
                        <p className="mt-3 text-xs">
                          Use <code>{"{"}{"{"} #if variable {"}"}{"}"}  ...  {"{"}{"{"} /if {"}"}{"}"}  </code> for conditional content
                        </p>
                      </CardContent>
                    </Card>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSave}
                        disabled={updateTemplate.isPending}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {updateTemplate.isPending ? "Saving..." : "Save Template"}
                      </Button>

                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline">
                            <Mail className="h-4 w-4 mr-2" />
                            Send Test Email
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Send Test Email</DialogTitle>
                            <DialogDescription>
                              This feature sends a test email to ricksblackcarservice@outlook.com
                            </DialogDescription>
                          </DialogHeader>
                          <p className="text-sm text-muted-foreground">
                            Test email functionality coming soon...
                          </p>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </AdminLayout>
    </ProtectedRoute>
  );
}
